import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { assistantQueryKeys as queryKeys } from '../api/queryKeys'
import {
  deleteChatThread,
  getChatThreads,
  renameChatThread,
  searchChatThreads,
} from '../api/chatThreads.api'
import type { ChatThreadsResponse } from '../api/contracts'

/** The caller's chat threads, most recently active first, with rename and
 * delete. Mutations answer with the fresh list, applied straight to the cache
 * (same shape as useCustomSkills). */
export function useChatThreads() {
  const queryClient = useQueryClient()

  const threadsQuery = useQuery({
    queryKey: queryKeys.assistantChatThreads,
    queryFn: getChatThreads,
  })

  const applyResponse = (data: ChatThreadsResponse) => {
    queryClient.setQueryData(queryKeys.assistantChatThreads, data)
    // Search results and folders carry titles, and may list a thread that's now gone.
    void queryClient.invalidateQueries({
      queryKey: [...queryKeys.assistantChatThreadsAll, 'search'],
    })
    void queryClient.invalidateQueries({ queryKey: queryKeys.assistantChatFolderThreadsAll })
  }

  const renameMutation = useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      renameChatThread(threadId, { title }),
    onSuccess: applyResponse,
  })

  const deleteMutation = useMutation({
    mutationFn: (threadId: string) => deleteChatThread(threadId),
    onSuccess: (data, threadId) => {
      applyResponse(data)
      queryClient.removeQueries({ queryKey: queryKeys.assistantChatThread(threadId) })
    },
  })

  return {
    threads: threadsQuery.data?.threads ?? [],
    isLoading: threadsQuery.isLoading,
    isError: threadsQuery.isError,
    retryLoad: threadsQuery.refetch,
    renameThread: renameMutation.mutateAsync,
    isRenaming: renameMutation.isPending,
    deleteThread: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  }
}

/** Typo-tolerant keyword search over the caller's own threads; idle (and
 * fetching nothing) while `query` is blank. */
export function useChatThreadSearch(query: string) {
  const trimmed = query.trim()
  const searchQuery = useQuery({
    queryKey: queryKeys.assistantChatThreadSearch(trimmed),
    queryFn: () => searchChatThreads(trimmed),
    enabled: trimmed.length > 0,
  })

  return {
    results: searchQuery.data?.results ?? [],
    isSearching: searchQuery.isFetching,
    error: searchQuery.error,
  }
}
